import Seo from "@/components/Seo";
import React, { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Upload, X } from "lucide-react";
import { FadeUp } from "@/components/motion";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";

const BRANDS = [
  "WhichSchoolAdvisor",
  "SchoolsCompared",
  "Top Schools Awards",
  "EdinformaPlus",
  "Education Intelligence Group",
  "WhichUniversityAdvisor",
  "General / Group",
];

const INQUIRY = ["Advertising", "Editorial", "Apply for a job", "Other"];

const CV_ACCEPT = ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const CV_MAX_MB = 5;

const fieldCls =
  "w-full bg-secondary border-2 border-transparent focus:border-primary rounded-xl outline-none px-4 py-3.5 text-base font-medium placeholder:text-foreground/40 transition-colors";

const Contact = () => {
  const [params] = useSearchParams();
  const role = params.get("role");
  const [form, setForm] = useState({
    name: "", email: "", company: "", brand: "",
    inquiry_type: role ? "Other" : "",
    message: role ? `I'd like to apply for the ${role} position.\n\n` : "",
    website: "", // honeypot
    math_answer: "",
  });
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Simple math challenge — regenerated on mount
  const challenge = useMemo(() => {
    const a = Math.floor(Math.random() * 8) + 2; // 2..9
    const b = Math.floor(Math.random() * 8) + 2;
    return { a, b };
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isJobApp = form.inquiry_type === "Apply for a job";

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const ext = f.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error("CV must be a PDF, DOC or DOCX file.");
      e.target.value = "";
      return;
    }
    if (f.size > CV_MAX_MB * 1024 * 1024) {
      toast.error(`CV must be under ${CV_MAX_MB} MB.`);
      e.target.value = "";
      return;
    }
    setCv(f);
  };

  const clearCv = () => {
    setCv(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.brand || !form.inquiry_type || !form.message) {
      return toast.error("Please complete all required fields.");
    }
    if (isJobApp && !cv) {
      return toast.error("Please attach your CV to apply.");
    }
    if (form.math_answer === "" || Number(form.math_answer) !== challenge.a + challenge.b) {
      return toast.error("Please answer the security question correctly.");
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("company", form.company || "");
      fd.append("brand", form.brand);
      fd.append("inquiry_type", form.inquiry_type);
      fd.append("message", form.message);
      fd.append("website", form.website); // honeypot
      fd.append("math_a", String(challenge.a));
      fd.append("math_b", String(challenge.b));
      fd.append("math_answer", String(form.math_answer));
      if (cv) fd.append("cv", cv);
      await api.post("/contact", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Message sent. We'll be in touch shortly.");
      setForm({ name: "", email: "", company: "", brand: "", inquiry_type: "", message: "", website: "", math_answer: "" });
      clearCv();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="pt-[76px]">
      <Seo
        title="Contact"
        path="/contact"
        description="Get in touch with Which Media — partnerships, editorial (SchoolsCompared & WhichSchoolAdvisor), commercial, consultancy, franchising and careers."
      />
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 pt-16 md:pt-24 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
        <div>
          <p className="kicker mb-6">Contact</p>
          <h1 className="headline-xl uppercase text-6xl md:text-8xl text-ink">
            Let&rsquo;s<br /><span className="text-primary">talk.</span>
          </h1>
          <p className="mt-8 max-w-md text-lg md:text-xl font-medium leading-snug text-foreground/80">
            Whether you&rsquo;re a school, an advertiser, a journalist or a future colleague — tell us what you
            need and choose the right team below.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              ["Partnerships", "davidwestley@which-media.com"],
              ["Editorial · SchoolsCompared", "editor@schoolscompared.com"],
              ["Editorial · WhichSchoolAdvisor", "editor@whichschooladvisor.com"],
              ["Commercial", "sales@which-media.com"],
              ["Consultancy", "Consultancy@EducationIntelligenceGroup.com"],
              ["Franchising", "Franchising@WhichSchoolAdvisor.com"],
              ["Offices", "Dubai, UAE · London, UK"],
            ].map(([k, v]) => (
              <div key={k} className="border-t-2 border-ink pt-3">
                <p className="kicker mb-1 !text-foreground/50">{k}</p>
                <p className="text-[15px] font-semibold text-ink break-all">{v}</p>
              </div>
            ))}
          </div>
        </div>

        <FadeUp>
          <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl border border-border p-6 md:p-8 space-y-5 shadow-sm">
            <input data-testid="contact-name" className={fieldCls} placeholder="Full name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
            <input data-testid="contact-email" type="email" className={fieldCls} placeholder="Email address *" value={form.email} onChange={(e) => set("email", e.target.value)} />
            <input data-testid="contact-company" className={fieldCls} placeholder="Company / School (optional)" value={form.company} onChange={(e) => set("company", e.target.value)} />

            <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
              <SelectTrigger data-testid="contact-brand" className={`${fieldCls} h-auto`}>
                <SelectValue placeholder="Which product / brand? *" />
              </SelectTrigger>
              <SelectContent>
                {BRANDS.map((b) => (<SelectItem key={b} value={b} data-testid={`brand-option-${b}`}>{b}</SelectItem>))}
              </SelectContent>
            </Select>

            <Select value={form.inquiry_type} onValueChange={(v) => set("inquiry_type", v)}>
              <SelectTrigger data-testid="contact-inquiry" className={`${fieldCls} h-auto`}>
                <SelectValue placeholder="Nature of enquiry? *" />
              </SelectTrigger>
              <SelectContent>
                {INQUIRY.map((t) => (<SelectItem key={t} value={t} data-testid={`inquiry-option-${t}`}>{t}</SelectItem>))}
              </SelectContent>
            </Select>

            {isJobApp && (
              <div data-testid="cv-upload-block">
                <label htmlFor="cv" className="block cursor-pointer">
                  <div className={`${fieldCls} flex items-center justify-between gap-3 !py-4 border-dashed border-foreground/25 hover:border-primary`}>
                    <span className="flex items-center gap-3 text-foreground/70">
                      <Upload size={18} />
                      {cv ? cv.name : "Upload your CV * — PDF, DOC or DOCX (max 5 MB)"}
                    </span>
                    {cv && (
                      <button type="button" onClick={(e) => { e.preventDefault(); clearCv(); }} data-testid="clear-cv" className="text-foreground/50 hover:text-destructive">
                        <X size={18} />
                      </button>
                    )}
                  </div>
                </label>
                <input
                  ref={fileInputRef}
                  id="cv"
                  data-testid="contact-cv"
                  type="file"
                  accept={CV_ACCEPT}
                  className="hidden"
                  onChange={onFileChange}
                />
              </div>
            )}

            <textarea data-testid="contact-message" rows={5} className={`${fieldCls} resize-none`} placeholder={isJobApp ? "A short note about the role you're interested in *" : "Your message *"} value={form.message} onChange={(e) => set("message", e.target.value)} />

            {/* Honeypot — visually hidden but present in DOM */}
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              name="website"
              value={form.website}
              onChange={(e) => set("website", e.target.value)}
              aria-hidden="true"
              className="absolute -left-[9999px] w-px h-px opacity-0"
            />

            {/* Math CAPTCHA */}
            <div className="flex items-center gap-4">
              <label className="font-sans text-sm font-semibold text-foreground/70 shrink-0" htmlFor="math_answer">
                Security check: what is {challenge.a} + {challenge.b}?
              </label>
              <input
                id="math_answer"
                data-testid="contact-math"
                type="number"
                inputMode="numeric"
                className={`${fieldCls} max-w-[120px]`}
                value={form.math_answer}
                onChange={(e) => set("math_answer", e.target.value)}
                placeholder="?"
                required
              />
            </div>

            <button
              data-testid="contact-submit"
              type="submit"
              disabled={loading}
              className="w-full inline-flex items-center justify-center gap-3 font-sans text-sm font-bold bg-primary text-primary-foreground rounded-full py-4 hover:bg-ink transition-colors disabled:opacity-40"
            >
              {loading ? "Sending..." : isJobApp ? "Send application" : "Send message"} <ArrowRight size={16} />
            </button>
          </form>
        </FadeUp>
      </div>
    </div>
  );
};

export default Contact;
